import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Lock, Shield, FileCheck, Cloud, Database, Eye, Play } from 'lucide-react';
import ancileProtectionImage from '../assets/images/ancile-protection.png';
import VideoForm from '../components/VideoForm/VideoForm';

const FeatureCard = ({ feature }) => {
  return (
    <motion.div
      className="relative overflow-hidden bg-white rounded-2xl shadow-md border border-gray-100 p-8"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center gap-4 mb-4">
        <div className="p-3 bg-[#EBF3FF] rounded-xl">
          <feature.icon className="w-6 h-6 text-[#4B83F2]" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
      </div>
      <p className="text-gray-600">{feature.description}</p>
    </motion.div>
  );
};

const PillarsCard = ({ pillar }) => {
  return (
    <motion.div
      className="relative overflow-hidden bg-white rounded-2xl shadow-md border border-gray-100 p-8"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: pillar.delay }}
    >
      <div className="mb-6">
        <div className="p-4 bg-[#EBF3FF] rounded-xl inline-block mb-4">
          <pillar.icon className="w-8 h-8 text-[#4B83F2]" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{pillar.title}</h3>
      </div>
      <ul className="space-y-3">
        {pillar.points.map((point, idx) => (
          <li key={idx} className="flex items-start ">
            <span className="text-[#4B83F2] mr-2">•</span>
            <p className="text-gray-700">{point}</p>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const Platform = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    organization: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    // Nothing needed here - we'll always show the form first
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Get the API URL from environment variable
      const scriptURL = "https://script.google.com/macros/s/AKfycbz_D9Ta8CKoZQoBrariausbsDVfoy080NIgDSFLd9SxVGnMRFRoCGXtWzq6JCS2fY0p/exec";
      
      // Create a FormData object
      const form = new FormData();
      form.append('timestamp', new Date().toISOString());
      form.append('videoId', 'platform-page-form');
      form.append('firstName', formData.username.split(' ')[0] || '');
      form.append('lastName', formData.username.split(' ').slice(1).join(' ') || '');
      form.append('email', formData.email);
      form.append('phoneNumber', formData.phone);
      form.append('organizationName', formData.organization);
      form.append('comments', '');
      
      // Use URLSearchParams for compatibility
      const params = new URLSearchParams();
      for (const pair of form.entries()) {
        params.append(pair[0], pair[1]);
      }
      
      // Create a URL with query string
      const url = scriptURL + '?' + params.toString();
      
      // Use a simple image approach to avoid CORS
      const img = document.createElement('img');
      img.style.display = 'none';
      img.src = url;
      document.body.appendChild(img);
      
      // Remove the image after a delay
      setTimeout(() => {
        document.body.removeChild(img);
      }, 5000);
    } catch (error) {
      // Handle error silently
    }
    
    setFormSubmitted(true);
    setShowVideo(true);
    setShowModal(false);
  };

  const handleVideoClick = () => {
    if (!showVideo) {
      setShowModal(true);
    }
  };

  const handleFormSubmit = (submittedFormData) => {
    try {
      // Get the API URL from environment variable
      const scriptURL = "https://script.google.com/macros/s/AKfycbz_D9Ta8CKoZQoBrariausbsDVfoy080NIgDSFLd9SxVGnMRFRoCGXtWzq6JCS2fY0p/exec";
      
      // Create a FormData object
      const form = new FormData();
      form.append('timestamp', new Date().toISOString());
      form.append('videoId', 'platform-demo');
      
      // The VideoForm component already provides firstName and lastName separately
      form.append('firstName', submittedFormData.firstName || '');
      form.append('lastName', submittedFormData.lastName || '');
      form.append('email', submittedFormData.email || '');
      form.append('phoneNumber', submittedFormData.phoneNumber || '');
      form.append('organizationName', submittedFormData.organizationName || '');
      form.append('comments', submittedFormData.comments || '');
      
      // Use URLSearchParams for compatibility
      const params = new URLSearchParams();
      for (const pair of form.entries()) {
        params.append(pair[0], pair[1]);
      }
      
      // Create a URL with query string
      const url = scriptURL + '?' + params.toString();
      
      // Use a simple image approach to avoid CORS
      const img = document.createElement('img');
      img.style.display = 'none';
      img.src = url;
      document.body.appendChild(img);
      
      // Remove the image after a delay
      setTimeout(() => {
        document.body.removeChild(img);
      }, 5000);
    } catch (error) {
      // Handle error silently
    }
    
    // Continue with showing the video
    localStorage.setItem('ancile_video_access', 'true');
    setShowVideo(true);
    setShowModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const pillars = [
    {
      title: "Absolute Privacy",
      icon: Lock,
      delay: 0.1,
      points: [
        "Know who (and what) has access to your data",
        "Own your data and AI/ML models in your environment",
        "Understand the extent of sprawl and orphaned data"
      ]
    },
    {
      title: "Complete Visibility",
      icon: Eye,
      delay: 0.3,
      points: [
        "See access paths to any data store",
        "Find and inventory repositories across cloud and geo boundaries",
        "Monitor, observe, and analyze access patterns"
      ]
    },
    {
      title: "Total Control",
      icon: Shield,
      delay: 0.5,
      points: [
        "Manage processes for Data discovery and governance",
        "Identify potential vulnerabilities and exposures",
        "Detect suspicious activities with generative AI"
      ]
    },
    {
      title: "Assure Compliance",
      icon: FileCheck,
      delay: 0.5,
      points: [
        "Automate and centralize GRC workflows",
        "Stay continuously audit-ready and compliant",
        "Gain real-time risk insights"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative w-full h-[500px] bg-gradient-background overflow-hidden">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-secondary/10 to-accent/10" />
        
        {/* Main content */}
        <div className="relative z-20 container mx-auto px-4 py-24 flex flex-col md:flex-row items-center h-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl mx-auto text-center md:text-left relative px-4 md:px-0"
          >
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-primary mb-4 md:mb-6 leading-tight">
              Secure your digital assets with ANCILE
            </h1>
            <p className="text-lg sm:text-xl text-white mb-8 max-w-2xl mx-auto md:mx-0 md:max-w-4xl">
              Enterprise-grade protection with advanced privacy controls, full visibility, and threat detection.
            </p>
            <button
              onClick={() => window.open('/assets/documents/AncileDSPMDataSheet.pdf', '_blank')}
              className="bg-gradient-primary hover:bg-gradient-secondary text-white px-8 py-4 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-glow"
            >
              Datasheet
            </button>
          </motion.div>
          <img 
            src={ancileProtectionImage} 
            alt="Ancile Protection" 
            className="hidden md:block w-96 h-auto md:ml-8 mt-8 md:mt-0" 
          />
        </div>
        
        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-background-dark to-transparent" />
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-background-dark to-transparent" />
      </div>

      {/* Pillars Section */}
      <div className=" mx-auto px-6 py-16">
        <div className="grid md:grid-cols-4 gap-8 mb-16">
          {pillars.map((pillar, idx) => (
            <PillarsCard key={idx} pillar={pillar} />
          ))}
        </div>

        {/* Platform Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Innovative Data Security and Privacy Management
          </h2>
          <p className="text-lg text-gray-600">
            ANCILE is designed to address the critical challenges of data protection, governance, 
            and compliance in multi-cloud environments. Effectively discover, classify, detect and 
            respond, ensure compliance, and govern your cloud data stores and identities.
          </p>
        </motion.div>

        {/* Platform Video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="rounded-xl overflow-hidden shadow-lg">
            {!showVideo ? (
              <div 
                className="relative cursor-pointer group" 
                onClick={handleVideoClick}
              >
                <img 
                  src="/assets/images/platform/platform-dashboard.png" 
                  alt="ANCILE Platform Demo" 
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center group-hover:bg-opacity-50 transition-all">
                  <div className="bg-white bg-opacity-90 rounded-full p-5 shadow-lg transform transition-transform group-hover:scale-110">
                    <Play className="w-10 h-10 text-[#4B83F2]" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
                  <h3 className="text-2xl font-bold text-white">Watch the ANCILE Platform Demo</h3>
                </div>
              </div>
            ) : (
              <div>
                <video 
                  src="/assets/videos/ancile-dashboard.mp4" 
                  controls
                  className="w-full h-auto"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
          </div>
        </motion.div>

        {/* Modal Form */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div 
              className="absolute inset-0 bg-black bg-opacity-70 backdrop-blur-sm" 
              onClick={closeModal}
            ></div>
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full mx-4 z-10 relative"> 
              <button 
                onClick={closeModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
              <VideoForm 
                videoId="platform-demo"
                onFormSubmit={handleFormSubmit}
              />
            </div>
          </div>
        )}

      </div>  

      {/* Call to Action */}
      <div className="bg-gradient-to-b from-white to-[#EBF3FF] py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Secure Your Organization's Most Valuable Asset
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              With ANCILE's advanced features and proactive approach to data protection, 
              you can confidently embrace multi-cloud deployments while maintaining compliance 
              and mitigating security risks.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => window.location.href = '/contact'}
                className="bg-[#4B83F2] hover:bg-[#3A72E1] text-white px-8 py-4 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-md w-full sm:w-auto"
              >
                Request a Demo
              </button>
              <a 
                href="https://aws.amazon.com/marketplace/pp/prodview-wpcgzaduqux7e" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center justify-center bg-gradient-background p-2 rounded-lg border-2 border-gray-800 hover:border-[#4B83F2] transition-all transform hover:scale-105 w-full sm:w-auto"
              >
                <img 
                  src="/assets/images/aws-marketplace.png" 
                  alt="Available on AWS Marketplace" 
                  className="h-10 w-auto"
                />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Platform; 